import React, { useState, useEffect, useMemo, useCallback } from "react";
import InitTrainingsLogic from "./Trainings.logic";
import { FaClock, FaUsers, FaCalendarTimes } from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "./Trainings.module.css";
import ConfigHelper from "../../config/Config";
import Loading from "../Loading/Loading.component";
import { formatBookingDate as formatDate, formatLongDate, formatBookingTime as formatTime, bookingDayKey, calendarDayKey } from "../../helpers/dateFormat";

const TrainingsComponent: React.FC = () => {
    const { user, selectedDate, trainings, trainers, locations, isLoading, setSelectedDate, onBook, onCancel, onChangeLocation, onSetTrainer, showClientsFromBooking } = InitTrainingsLogic();
    const [filteredTrainings, setFilteredTrainings] = useState<any[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
    const [repeatDays, setRepeatDays] = useState<{ [key: string]: boolean }>({});
    const [showModal, setShowModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
    // Broj prijavljenih koji admin vidi u trenutku klika na Otkaži - server
    // prekida otkazivanje ako se u međuvremenu neko zakazao
    const [cancelExpectedClients, setCancelExpectedClients] = useState<number>(0);

    const dayMapping: Record<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun", string> = useMemo(() => ({
        Mon: "Pon",
        Tue: "Uto",
        Wed: "Sre",
        Thu: "Cet",
        Fri: "Pet",
        Sat: "Sub",
        Sun: "Ned"
    }), []);

    // Boje kalendara (zeleno/crveno/narandzasto) vidi SAMO admin. Treneri
    // samo klikom na dan gledaju ko je prijavljen po terminu, bez boja.
    const isAdmin = user?.username === ConfigHelper.getAdminUsername();

    const tileClassName = useCallback(({ date }: any) => {
        if (!isAdmin) return null;

        const formattedDate = calendarDayKey(date);

        // Proveri da li je datum selektovan i postavi plavu pozadinu
        if (selectedDate && formattedDate === calendarDayKey(selectedDate)) {
            return null; // Dodaj klasu za selektovani datum
        }

        // Filtriraj termine za trenutni datum
        const trainingsForDate = trainings.filter(
            (t) => bookingDayKey(t.date) === formattedDate
        );

        if (trainingsForDate.some((t) => t.cancelled)) {
            return styles.orangeHighlightedDate; // Narandžasto ako postoji otkazani termin
        }

        if (trainingsForDate.length > 0 && trainingsForDate.every((t) => (t.clients ? t.clients.length : 0) >= t.maxNumberOfClients)) {
            return styles.redHighlightedDate; // Crveno ako su svi termini puni
        }

        if (trainingsForDate.some((t) => ((t.clients ? t.clients.length : 0) < t.maxNumberOfClients) && !t.cancelled)) {
            return styles.highlightedDate; // Zeleno ako postoji slobodan termin
        }
        return null;
    }, [selectedDate, trainings, isAdmin]);

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        const selectedDateString = calendarDayKey(date);

        // Get general trainings for the selected date, excluding those that are in client.trainings
        const generalTrainings = trainings
            .filter((b) =>
                bookingDayKey(b.date) === selectedDateString
            );

        // Combine client-specific and general trainings
        setFilteredTrainings([...generalTrainings]);
    };

    // Na ulasku u Grupe odmah selektuj današnji dan (i za admina i za trenera)
    // da se odmah vide današnji termini bez klika na kalendar.
    useEffect(() => {
        if (!selectedDate) setSelectedDate(new Date());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Kada se termini ponovo učitaju (npr. posle promene trenera), osveži i
    // listu za izabrani datum - inače bi prikazivala stare podatke.
    useEffect(() => {
        if (!selectedDate) return;
        const selectedDateString = calendarDayKey(selectedDate);
        setFilteredTrainings(
            trainings.filter((b) => bookingDayKey(b.date) === selectedDateString)
        );
    }, [trainings, selectedDate]);

    const handleBookingClick = (booking: any) => {
        setSelectedBooking(booking);
        // Dan u nedelji iz UTC polja (wall clock zapis termina)
        const selectedDayEng = new Date(booking.date).toLocaleDateString("en-US", {
            weekday: "short",
            timeZone: "UTC",
        }) as keyof typeof dayMapping;
        const selectedDay = dayMapping[selectedDayEng];
        setRepeatDays((prev) => ({ ...prev, [selectedDay]: true }));
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedBooking(null);
        setRepeatDays({});
    };

    /* const shouldShowSaturday = () => {
        if (selectedBooking) {
            const bookingDate = new Date(selectedBooking.date);
            const dayOfWeek = bookingDate.getUTCDay(); // 0 = Sunday, 6 = Saturday
            const hour = bookingDate.getUTCHours();

            // Proveri da li je subota
            return dayOfWeek === 6;
        }
        return false;
    }; */


    const formatDateString = (date: Date) => formatLongDate(date);

    /* const handleDayToggle = (day: string) => {
        setRepeatDays((prevState) => ({
            ...prevState,
            [day]: !prevState[day],
        }));
    }; */


    useEffect(() => {
        if (selectedBooking) {
            const selectedDayEng = new Date(selectedBooking.date).toLocaleDateString("en-US", {
                weekday: "short",
                timeZone: "UTC",
            }) as keyof typeof dayMapping;
            const selectedDay = dayMapping[selectedDayEng];
            setRepeatDays((prevState) => ({
                ...prevState,
                [selectedDay]: true,
            }));
        }
    }, [selectedBooking, dayMapping]);

    const handleCancelClick = (booking: any) => {
        setSelectedBookingId(booking.id);
        setCancelExpectedClients(booking.clients ? booking.clients.length : 0);
        setShowModal(true);
    };

    const confirmCancel = () => {
        if (selectedBookingId !== null) {
            onCancel(selectedBookingId, cancelExpectedClients);
            setSelectedDate(null)
        }
        setShowModal(false);
    };

    const closeModalCancel = () => setShowModal(false);

    return (
        <div className={styles.container}>
            {/* Modal for Confirmation */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '5px',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <p>Da li ste sigurni da želite da otkažete ovaj trening?</p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginTop: '20px'
                        }}>
                            <button onClick={closeModalCancel} className="btn-secondary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>Ne</button>
                            <button onClick={confirmCancel} className="btn-primary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>Da</button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.padding50}>
                <label htmlFor="location">Lokacija</label>
                <select id="location" aria-label={"Izaberi lokaciju"} onChange={onChangeLocation} name="location">
                    {locations.map((l: string, index: number) => (
                        <option key={index} value={l}>{l}</option>
                    ))}
                </select>
            </div>

            {isLoading ? (
                <Loading />
            ) : (<>
                <div className={styles.calendarContainer}>
                    <Calendar
                        onClickDay={handleDateClick}
                        tileClassName={tileClassName}
                        view="month"
                        minDetail="month"
                        locale="sr-Latn"
                        minDate={new Date()}
                        value={selectedDate}
                        className={styles.reactCalendar}
                    />
                </div>

                <br />

                {selectedDate && (
                    <div className={styles.selectedDateContainer}>
                        <h3>{formatDateString(selectedDate)}</h3>

                        {filteredTrainings.length > 0 ? (
                            <div className={styles.trainingsList}>
                                {filteredTrainings.map((b) => {
                                    const clientsCount = b.clients ? b.clients.length : 0;
                                    const hasTrialClient = b.clients?.some((c: any) => c.trial);
                                    const hasNewClient = b.clients?.some((c: any) =>
                                        !c.trial &&
                                        (!c.payments || c.payments.length === 0) &&
                                        (!c.trainings || c.trainings.length === 0)
                                    );
                                    const borderStyle = clientsCount === 0
                                        ? { border: "2px solid red" }
                                        : clientsCount === 1
                                            ? { border: "2px solid #e6c619" }
                                            : {};
                                    return (
                                        // Kartica je kolona: gornji red (vreme/detalji/dugme) + trener red pune sirine ispod, sve UNUTAR kartice
                                        <div key={b.id} className={styles.bookingItem} style={{ ...borderStyle, flexDirection: "column", alignItems: "stretch", gap: 10 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                                <div className={styles.bookingTime}>
                                                    <FaClock className={styles.icon} />
                                                    {formatTime(b.date)}
                                                </div>
                                                <div className={styles.bookingDetails} onClick={() => { if (clientsCount > 0) showClientsFromBooking(b); }} style={clientsCount === 0 ? { cursor: "default" } : undefined}>
                                                    <h4>{b.name}</h4>
                                                    <p className={styles.bookingClientCount}>
                                                        <FaUsers className={styles.icon} />
                                                        Zauzeto: {clientsCount}/{b.maxNumberOfClients}
                                                    </p>
                                                    {hasTrialClient && <p style={{color: "#8e44ad", fontWeight: "bold", fontSize: "0.85em", margin: 0}}>Probni</p>}
                                                    {hasNewClient && <p style={{color: "orange", fontWeight: "bold", fontSize: "0.85em", margin: 0}}>Novi klijent</p>}
                                                </div>
                                                {user?.username === ConfigHelper.getAdminUsername() &&
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent parent onClick event if necessary
                                                            !b.cancelled ? handleCancelClick(b) : handleBookingClick(b); // Toggle based on booking type
                                                        }}
                                                        className={styles.scheduleButton}
                                                        style={!b.cancelled ? { backgroundColor: 'var(--danger)', color: 'var(--white)' } : {}}
                                                    >
                                                        {!b.cancelled ? "Otkaži" : "Zakaži"}
                                                    </button>
                                                }
                                            </div>
                                            {/* Trener red pune sirine unutar kartice - da se pun naziv vidi */}
                                            <select
                                                aria-label="Trener koji drži trening"
                                                value={b.userId ?? ""}
                                                onChange={(e) => onSetTrainer(b.id, e.target.value === "" ? null : +e.target.value)}
                                                style={{ fontSize: "0.9em", padding: "8px 10px", width: "100%" }}
                                            >
                                                <option value="">Trener nije određen</option>
                                                {b.userId && !trainers.some((tr: any) => tr.id === b.userId) && (
                                                    <option value={b.userId}>
                                                        {b.user ? `${b.user.firstName} ${b.user.lastName}` : "Nepoznat trener"}
                                                    </option>
                                                )}
                                                {trainers.map((tr: any) => (
                                                    <option key={tr.id} value={tr.id}>{tr.firstName} {tr.lastName}</option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.noTrainingsContainer}>
                                <FaCalendarTimes className={styles.noTrainingsIcon} />
                                <h3 className={styles.noTrainingsMessage}>Nema aktivnosti za izabrani datum</h3>
                            </div>
                        )}
                    </div>
                )}

                {modalVisible && selectedBooking && (
                    <div className={styles.modalOverlay} onClick={closeModal}>
                        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            {/* Podebljane su samo labele, vrednosti normalne */}
                            <p style={{ fontWeight: 400 }}><b>Trening:</b> {selectedBooking.name}</p>
                            <p style={{ fontWeight: 400 }}><b>Datum:</b> {formatDate(selectedBooking.date)}</p>
                            <p style={{ fontWeight: 400 }}><b>Vreme:</b> {formatTime(selectedBooking.date)}</p>

                            {/* <h4>Trening se ponavlja danima:</h4>
                        <div className={styles.repeatDaysContainer}>
                            {Object.keys(dayMappingAlwaysDisplayed).map((dayEng) => {
                                const daySrpski = dayMapping[dayEng as keyof typeof dayMapping];
                                return (
                                    <div key={daySrpski} className={styles.repeatDay}>
                                        <input
                                            type="checkbox"
                                            checked={repeatDays[daySrpski] || false}
                                            onChange={() => handleDayToggle(daySrpski)}
                                        />
                                        <label>{daySrpski}</label>
                                    </div>
                                );
                            })}
                            {shouldShowSaturday() && (
                                <div key="Sub" className={styles.repeatDay}>
                                    <input
                                        type="checkbox"
                                        checked={repeatDays["Sub"] || false}
                                        onChange={() => handleDayToggle("Sub")}
                                    />
                                    <label>Sub</label>
                                </div>
                            )}
                        </div> */}

                            <div className={styles.modalButtons}>
                                <button className={styles.saveButton} onClick={() => { onBook({ id: selectedBooking.id, repeatDays }); setSelectedDate(null); closeModal() }}>
                                    Sačuvaj
                                </button>
                                <button className={styles.cancelButton} onClick={closeModal}>
                                    Odustani
                                </button>
                            </div>
                        </div>
                    </div>
                )
                }
            </>
            )}
        </div >
    )
};

export default TrainingsComponent;
